// CookieConsent — GDPR/CCPA-compliant banner + manage-preferences
// modal. Drives Google Consent Mode v2 via window.gtag and stores
// the user's choice in localStorage so the banner only shows once.
//
// Storage key: "vauren_cookie_consent" (values: "accepted" |
// "declined" | "custom").  A legacy hyphen key from earlier deploys
// ("vauren-cookie-consent") is auto-migrated on mount so returning
// visitors don't see the banner a second time.
//
// Returning-visitor consent re-application happens on mount: if the
// stored value is "accepted" we grant everything, "declined" denies
// everything, "custom" replays the per-category map saved alongside.
//
// Mounted in App.tsx inside the Router.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const CONSENT_KEY = "vauren_cookie_consent";
const LEGACY_KEY = "vauren-cookie-consent";
const CUSTOM_PREFS_KEY = "vauren_cookie_preferences";

type ChoiceState = "accepted" | "declined" | "custom";

interface ConsentPreferences {
  analytics: boolean;
  advertising: boolean;
  personalisation: boolean;
}

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
  }
}

const DEFAULT_PREFS: ConsentPreferences = {
  analytics: true,
  advertising: false,
  personalisation: false,
};

const SERIF = '"Cormorant Garamond", serif';
const SANS = '"DM Sans", system-ui, sans-serif';
const MONO = '"DM Mono", ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace';

const G = {
  panel: "#1A1714",
  gold: "#997E54",
  goldLine: "rgba(153,126,84,0.3)",
  goldBorder: "rgba(153,126,84,0.2)",
  cream: "#F5F0EB",
  cream65: "rgba(245,240,235,0.65)",
  cream50: "rgba(245,240,235,0.50)",
  cream20: "rgba(245,240,235,0.20)",
  cream15: "rgba(245,240,235,0.15)",
  charcoal: "#0D0D0B",
} as const;

const grantFromPrefs = (prefs: ConsentPreferences) => ({
  analytics_storage: prefs.analytics ? "granted" : "denied",
  ad_storage: prefs.advertising ? "granted" : "denied",
  ad_user_data: prefs.advertising ? "granted" : "denied",
  ad_personalization: prefs.personalisation ? "granted" : "denied",
});

const applyConsent = (prefs: ConsentPreferences) => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("consent", "update", grantFromPrefs(prefs));
};

const readChoice = (): { choice: ChoiceState | null; prefs: ConsentPreferences } => {
  if (typeof localStorage === "undefined") {
    return { choice: null, prefs: DEFAULT_PREFS };
  }
  // Migrate legacy hyphen-key writes from earlier deploys.
  let raw = localStorage.getItem(CONSENT_KEY);
  if (!raw) {
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy === "accepted" || legacy === "declined") {
      raw = legacy;
      try {
        localStorage.setItem(CONSENT_KEY, legacy);
        localStorage.removeItem(LEGACY_KEY);
      } catch {
        // ignore quota / private-mode
      }
    }
  }
  if (raw !== "accepted" && raw !== "declined" && raw !== "custom") {
    return { choice: null, prefs: DEFAULT_PREFS };
  }
  let prefs: ConsentPreferences = DEFAULT_PREFS;
  if (raw === "accepted") {
    prefs = { analytics: true, advertising: true, personalisation: true };
  } else if (raw === "declined") {
    prefs = { analytics: false, advertising: false, personalisation: false };
  } else if (raw === "custom") {
    try {
      const stored = localStorage.getItem(CUSTOM_PREFS_KEY);
      if (stored) prefs = { ...DEFAULT_PREFS, ...JSON.parse(stored) };
    } catch {
      prefs = DEFAULT_PREFS;
    }
  }
  return { choice: raw, prefs };
};

export const hasConsent = (): boolean => {
  const { choice, prefs } = readChoice();
  if (choice === "accepted") return true;
  if (choice === "custom") return prefs.analytics;
  return false;
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [prefs, setPrefs] = useState<ConsentPreferences>(DEFAULT_PREFS);

  useEffect(() => {
    const { choice, prefs: storedPrefs } = readChoice();
    if (choice === null) {
      setVisible(true);
      return;
    }
    setPrefs(storedPrefs);
    // Re-apply the previous decision on every load so a returning
    // visitor's GA4 / Ads data flow resumes immediately rather than
    // waiting for the next consent interaction.
    applyConsent(storedPrefs);
  }, []);

  const persist = (choice: ChoiceState, next: ConsentPreferences) => {
    try {
      localStorage.setItem(CONSENT_KEY, choice);
      if (choice === "custom") {
        localStorage.setItem(CUSTOM_PREFS_KEY, JSON.stringify(next));
      } else {
        localStorage.removeItem(CUSTOM_PREFS_KEY);
      }
    } catch {
      // ignore quota / private-mode
    }
    applyConsent(next);
    if (choice === "accepted" || (choice === "custom" && next.analytics)) {
      // Wake any analytics module that gated itself on consent.
      window.dispatchEvent(new CustomEvent("vauren:consent-accepted"));
    }
  };

  const accept = () => {
    const next: ConsentPreferences = {
      analytics: true,
      advertising: true,
      personalisation: true,
    };
    setPrefs(next);
    persist("accepted", next);
    setShowModal(false);
    setVisible(false);
  };

  const decline = () => {
    const next: ConsentPreferences = {
      analytics: false,
      advertising: false,
      personalisation: false,
    };
    setPrefs(next);
    persist("declined", next);
    setShowModal(false);
    setVisible(false);
  };

  const savePrefs = () => {
    const allOn = prefs.analytics && prefs.advertising && prefs.personalisation;
    const allOff = !prefs.analytics && !prefs.advertising && !prefs.personalisation;
    const choice: ChoiceState = allOn ? "accepted" : allOff ? "declined" : "custom";
    persist(choice, prefs);
    setShowModal(false);
    setVisible(false);
  };

  // Static button styles, memoised so we don't allocate on every
  // render (banner re-renders on each toggle in the modal).
  const styles = useMemo(
    () => ({
      manage: {
        background: "transparent",
        border: "none",
        color: G.gold,
        fontFamily: SANS,
        fontSize: 12,
        fontWeight: 500,
        textDecoration: "underline",
        cursor: "pointer",
        padding: 0,
        minHeight: 36,
      } as React.CSSProperties,
      decline: {
        background: "transparent",
        border: `1px solid ${G.cream15}`,
        color: G.cream50,
        fontFamily: SANS,
        fontSize: 13,
        fontWeight: 500,
        padding: "10px 22px",
        borderRadius: 2,
        cursor: "pointer",
        minHeight: 40,
        transition: "color 160ms ease, border-color 160ms ease",
      } as React.CSSProperties,
      accept: {
        background: G.gold,
        color: G.charcoal,
        border: "none",
        fontFamily: SERIF,
        fontSize: 16,
        fontWeight: 500,
        letterSpacing: "0.01em",
        padding: "10px 26px",
        borderRadius: 2,
        cursor: "pointer",
        minHeight: 40,
        transition: "filter 160ms ease",
      } as React.CSSProperties,
    }),
    [],
  );

  if (!visible && !showModal) return null;

  return (
    <>
      {visible && (
        <div
          className="vauren-cookie-banner"
          role="region"
          aria-label="Cookie consent"
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            background: G.panel,
            borderTop: `1px solid ${G.goldLine}`,
            padding: "20px 32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 28,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: 280, maxWidth: 620 }}>
            <p
              style={{
                fontFamily: MONO,
                fontSize: 9,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                color: G.gold,
                margin: 0,
                marginBottom: 8,
              }}
            >
              COOKIE PREFERENCES
            </p>
            <p
              style={{
                fontFamily: SANS,
                fontSize: 13,
                color: G.cream65,
                lineHeight: 1.6,
                margin: 0,
                maxWidth: 520,
              }}
            >
              Vauren uses cookies to understand how visitors use the platform
              and to improve your experience. Analytics data helps us build a
              better product.{" "}
              <Link
                to="/privacy"
                style={{ color: G.gold, textDecoration: "underline" }}
              >
                Privacy policy
              </Link>
              .
            </p>
          </div>
          <div
            className="vauren-cookie-buttons"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexShrink: 0,
            }}
          >
            <button
              type="button"
              onClick={() => setShowModal(true)}
              style={styles.manage}
            >
              Manage preferences
            </button>
            <button
              type="button"
              onClick={decline}
              style={styles.decline}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = G.cream;
                e.currentTarget.style.borderColor = G.cream50;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = G.cream50;
                e.currentTarget.style.borderColor = G.cream15;
              }}
            >
              Decline
            </button>
            <button
              type="button"
              onClick={accept}
              style={styles.accept}
              onMouseEnter={(e) => {
                e.currentTarget.style.filter = "brightness(1.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.filter = "none";
              }}
            >
              Accept all
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <PreferencesModal
          prefs={prefs}
          onChange={setPrefs}
          onClose={() => setShowModal(false)}
          onSave={savePrefs}
        />
      )}

      {/* dangerouslySetInnerHTML avoids React-managed text node inside <style>,
          which can throw NotFoundError("removeChild") during unmount if a
          browser extension or animation lib mutates surrounding DOM. */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media (max-width: 767px) {
          .vauren-cookie-banner {
            flex-direction: column !important;
            align-items: stretch !important;
            padding: 16px 20px !important;
            gap: 16px !important;
          }
          .vauren-cookie-buttons {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 10px !important;
            width: 100%;
          }
          .vauren-cookie-buttons > button {
            width: 100%;
          }
        }
      `,
        }}
      />
    </>
  );
};

/* ─── Preferences modal ─────────────────────────────────────── */

interface PreferencesModalProps {
  prefs: ConsentPreferences;
  onChange: (next: ConsentPreferences) => void;
  onClose: () => void;
  onSave: () => void;
}

const PreferencesModal = ({
  prefs,
  onChange,
  onClose,
  onSave,
}: PreferencesModalProps) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cookie preferences"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        background: "rgba(10,10,10,0.72)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: G.panel,
          border: `1px solid ${G.goldBorder}`,
          borderRadius: 8,
          padding: 40,
          maxWidth: 480,
          width: "100%",
          maxHeight: "calc(100vh - 40px)",
          overflowY: "auto",
        }}
      >
        <p
          style={{
            fontFamily: MONO,
            fontSize: 9,
            letterSpacing: "0.3em",
            textTransform: "uppercase",
            color: G.gold,
            margin: 0,
            marginBottom: 8,
          }}
        >
          COOKIE PREFERENCES
        </p>
        <h2
          style={{
            fontFamily: SERIF,
            fontWeight: 400,
            fontSize: 26,
            color: G.cream,
            margin: "0 0 24px",
            lineHeight: 1.15,
          }}
        >
          Choose what we collect.
        </h2>

        <ToggleRow
          label="Essential cookies"
          description="Required for the platform to function. Cannot be disabled."
          on={true}
          disabled
          rightLabel="Required"
        />
        <ToggleRow
          label="Analytics"
          description="Helps us understand how the platform is used so we can improve it."
          on={prefs.analytics}
          onToggle={() => onChange({ ...prefs, analytics: !prefs.analytics })}
        />
        <ToggleRow
          label="Advertising"
          description="Allows our advertising partners to measure campaign performance."
          on={prefs.advertising}
          onToggle={() => onChange({ ...prefs, advertising: !prefs.advertising })}
        />
        <ToggleRow
          label="Personalisation"
          description="Remembers your preferences across visits."
          on={prefs.personalisation}
          onToggle={() =>
            onChange({ ...prefs, personalisation: !prefs.personalisation })
          }
        />

        <div
          style={{
            display: "flex",
            gap: 12,
            justifyContent: "flex-end",
            marginTop: 28,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: `1px solid ${G.cream15}`,
              color: G.cream50,
              fontFamily: SANS,
              fontSize: 13,
              fontWeight: 500,
              padding: "10px 22px",
              borderRadius: 2,
              cursor: "pointer",
              minHeight: 40,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            style={{
              background: G.gold,
              color: G.charcoal,
              border: "none",
              fontFamily: SERIF,
              fontSize: 16,
              fontWeight: 500,
              letterSpacing: "0.01em",
              padding: "10px 26px",
              borderRadius: 2,
              cursor: "pointer",
              minHeight: 40,
            }}
          >
            Save preferences
          </button>
        </div>
      </div>
    </div>
  );
};

interface ToggleRowProps {
  label: string;
  description: string;
  on: boolean;
  disabled?: boolean;
  rightLabel?: string;
  onToggle?: () => void;
}

const ToggleRow = ({
  label,
  description,
  on,
  disabled,
  rightLabel,
  onToggle,
}: ToggleRowProps) => (
  <div
    style={{
      display: "flex",
      gap: 16,
      alignItems: "flex-start",
      padding: "16px 0",
      borderBottom: `1px solid ${G.cream15}`,
    }}
  >
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontFamily: SANS,
          fontSize: 14,
          fontWeight: 600,
          color: G.cream,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: SANS,
          fontSize: 12,
          color: G.cream65,
          lineHeight: 1.55,
        }}
      >
        {description}
      </div>
    </div>
    {disabled ? (
      <span
        aria-hidden
        style={{
          fontFamily: MONO,
          fontSize: 9,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: G.gold,
          flexShrink: 0,
          marginTop: 4,
        }}
      >
        {rightLabel ?? "Required"}
      </span>
    ) : (
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        onClick={onToggle}
        style={{
          flexShrink: 0,
          width: 40,
          height: 22,
          borderRadius: 11,
          border: "none",
          padding: 0,
          background: on ? G.gold : G.cream20,
          position: "relative",
          cursor: "pointer",
          transition: "background 160ms ease",
        }}
      >
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: 2,
            left: on ? 20 : 2,
            width: 18,
            height: 18,
            borderRadius: "50%",
            background: G.cream,
            transition: "left 160ms ease",
            boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
          }}
        />
      </button>
    )}
  </div>
);

export default CookieConsent;
